import React from 'react'
import { useIntl } from 'react-intl'

import FilterAltIcon from '@mui/icons-material/FilterAlt'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'

import useSearchState from '@/lib/hook/useSearchState'

// Noms des facettes ES exposees par le panneau de gauche (cles de facets_raw =
// champs de l'index store_products). Centralises ici pour les usages carte.
export const FACET_BRAND = 'Marques'
export const FACET_VENDOR = 'Vendu par'

type Props = {
  /** Nom de la facette du panneau de gauche (cle facets_raw, ex: 'Marques') */
  facetName: string
  /** Valeur du bucket a appliquer — doit matcher exactement la valeur de facette */
  facetValue?: string | null
  /** Libelle du tooltip (defaut : facetValue) */
  label?: string
  /**
   * Actif uniquement dans un contexte page de resultats avec panneau de facettes
   * (le parent decide). Hors contexte : rend les children nus, sans bouton.
   */
  enabled?: boolean
  /**
   * Position d'apparition du bouton par rapport a la valeur (defaut 'right',
   * dans le flux). 'below' : en ABSOLU sous la valeur (aucun decalage du texte
   * au survol) — pour les colonnes serrees type "Vendu par".
   */
  side?: 'right' | 'below'
  /** Classes additionnelles sur le bouton (ex: decalage si icone hover voisine) */
  buttonClassName?: string
  children: React.ReactNode
}

/**
 * Wrappe une valeur affichee sur une carte produit (marque, vendeur...) et fait
 * apparaitre au survol un bouton entonnoir qui applique la valeur comme filtre
 * de facette du panneau de gauche (meme setter que les checkboxes : la facette
 * apparait cochee et la liste se re-filtre).
 *
 * Anti-doublon lien/filtre : les children (souvent un <Link>) restent intacts et
 * gardent leur navigation ; le bouton est un SIBLING du lien (jamais un enfant,
 * pas de <a> imbrique) avec preventDefault + stopPropagation au clic.
 *
 * Mobile / ecrans tactiles : pas de survol — le bouton est volontairement omis
 * (`@media (hover: hover)` requis) pour ne pas encombrer les cartes.
 */
const FilterByHover: React.FC<Props> = ({
  facetName,
  facetValue,
  label,
  enabled = true,
  side = 'right',
  buttonClassName = '',
  children,
}) => {
  const { filtersApplied, setFilterValue } = useSearchState()
  const { formatMessage } = useIntl()

  const value = (facetValue ?? '').trim()

  if (!enabled || !value) return <>{children}</>

  const isActive = filtersApplied.some(f => f.filter_id === facetName && f.value === value)
  const title = formatMessage(
    { id: isActive ? 'web_retirer_filtre_valeur' : 'web_filtrer_par_valeur' },
    { label: label ?? value }
  )

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    // Cible de clic separee du lien voisin : aucune navigation ici, et on bloque
    // la remontee vers les handlers de la carte (tracking clic produit, etc.)
    event.preventDefault()
    event.stopPropagation()
    setFilterValue(facetName, value, !isActive)
  }

  const Icon = isActive ? FilterAltIcon : FilterAltOutlinedIcon

  /* Icône + petit texte "Filtrer par", révélés UNIQUEMENT au survol.
     side='right' (défaut) : dans le flux après la valeur (hidden -> inline-flex,
     aucune largeur réservée au repos).
     side='below' : en ABSOLU sous la valeur — zéro décalage du texte au survol,
     pour les colonnes serrées (ex: Vendu par, suivi du Pcb) ; le pt-0.5 assure
     la continuité de survol jusqu'au bouton.
     Typo alignée sur les petits textes du thème (text-xs, sans gras).
     Actif = couleur primaire + icône pleine, le clic retire. */
  const button = (
    <button
      type="button"
      onClick={handleClick}
      title={title}
      aria-label={title}
      aria-pressed={isActive}
      className={`hidden group-hover/facetfilter:inline-flex focus-visible:inline-flex items-center gap-0.5 shrink-0 bg-transparent border-0 p-0 align-middle cursor-pointer ${
        side === 'below' ? 'absolute top-full left-0 pt-0.5 z-10' : 'ml-1'
      } ${isActive ? 'text-primary' : 'text-gray-400 hover:text-primary'} ${buttonClassName}`}
    >
      <Icon style={{ fontSize: 13 }} />
      <span className="text-xs whitespace-nowrap leading-none">
        {formatMessage({ id: 'web_filtrer_par_bouton' })}
      </span>
    </button>
  )

  return (
    <span className="group/facetfilter relative inline-flex items-center max-w-full">
      {children}
      {button}
    </span>
  )
}

export default FilterByHover
